import glossaryKey from './glossaryKey';

/**
 * A term as /api/champis-glossary-terms lists it (GlossaryTerms, PHP).
 */

// A word: letters and digits, hyphens inside ("sub-libres") - an
// apostrophe separates two ("d'anastomose").
const WORD = /[\p{L}\p{M}\p{N}]+(?:[-‐‑][\p{L}\p{M}\p{N}]+)*/gu;

// What may separate the words of a term: spaces and apostrophes, not a
// comma or a full stop.
const GAP = /^[\s'’ʼ]+$/u;

// Text that isn't the post's own prose: a link (a species, a mention…), code,
// a quote of another post, a spoiler, an uploaded file's name.
const SKIPPED = 'a, code, pre, kbd, samp, script, style, button, textarea, select, blockquote, .spoiler, .ChampisGlossaryTerm, [data-s9e-mediaembed], [class*="FoFUpload"], [class*="fof-upload"]';
export class GlossaryIndex {
  terms = (() => new Map())();
  byKey = (() => new Map())();
  constructor(terms) {
    let maxWords = 0;
    for (const term of terms) {
      this.terms.set(term.id, term);
      for (const form of term.forms) {
        this.byKey.set(glossaryKey(form), term);
        maxWords = Math.max(maxWords, form.match(WORD)?.length ?? 0);
      }
    }
    this.maxWords = maxWords;
  }
  find(key) {
    return this.byKey.get(key);
  }
}
/**
 * The glossary terms of a text, each once: none already in `seen`, which
 * they're added to. The longest first ("lames libres" rather than a
 * "lame").
 */
export function findGlossaryTerms(text, index, seen) {
  const words = Array.from(text.matchAll(WORD), match => ({
    start: match.index,
    end: match.index + match[0].length,
    key: glossaryKey(match[0])
  }));
  const found = [];
  for (let i = 0; i < words.length;) {
    let longest = null;
    let key = '';
    for (let n = 0; n < index.maxWords && i + n < words.length; n++) {
      if (n > 0 && !GAP.test(text.slice(words[i + n - 1].end, words[i + n].start))) break;
      key += words[i + n].key;
      const term = index.find(key);
      if (term) longest = {
        length: n + 1,
        term
      };
    }
    if (!longest) {
      i++;
      continue;
    }
    if (!seen.has(longest.term.id)) {
      seen.add(longest.term.id);
      found.push({
        start: words[i].start,
        end: words[i + longest.length - 1].end,
        term: longest.term
      });
    }
    i += longest.length;
  }
  return found;
}

/**
 * A post's HTML with the first occurrence of each glossary term in a
 * <span class="ChampisGlossaryTerm">, which glossaryPopover.ts shows the
 * definition of. The same string when there's none.
 */
export default function annotateGlossaryTerms(html, index) {
  const template = document.createElement('template');
  template.innerHTML = html;
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) {
    const text = walker.currentNode;
    if (!text.parentElement?.closest(SKIPPED)) texts.push(text);
  }
  const seen = new Set();
  let changed = false;
  for (const text of texts) {
    const found = findGlossaryTerms(text.data, index, seen);
    if (!found.length) continue;
    const fragment = document.createDocumentFragment();
    let last = 0;
    for (const {
      start,
      end,
      term
    } of found) {
      const span = document.createElement('span');
      span.className = 'ChampisGlossaryTerm';
      span.tabIndex = 0;
      span.setAttribute('role', 'button');
      span.dataset.glossaryTerm = term.id;
      span.textContent = text.data.slice(start, end);
      fragment.append(text.data.slice(last, start), span);
      last = end;
    }
    fragment.append(text.data.slice(last));
    text.replaceWith(fragment);
    changed = true;
  }
  return changed ? template.innerHTML : html;
}
flarum.reg.add('champis-others', 'forum/utils/annotateGlossaryTerms', annotateGlossaryTerms);